import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, ChevronsUpDown, Plus, Star } from "lucide-react";
import { DealershipStatusSchema, type Dealership } from "@lumika/shared";
import { Link, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  SearchInput,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  operationalStatusVariant,
} from "@lumika/ui";
import { adminApi } from "../api/admin";

type StoredDealership = Pick<Dealership, "id" | "name" | "status">;

const RECENT_STORAGE_KEY = "lumika-admin:recent-dealerships";
const PINNED_STORAGE_KEY = "lumika-admin:pinned-dealerships";
const MAX_RECENT = 5;
const MAX_PINNED = 10;

export function DealershipSwitcher({
  activeDealer,
}: {
  activeDealer: Pick<Dealership, "id" | "name" | "status"> | null;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [recent, setRecent] = useState<StoredDealership[]>(() =>
    readStoredDealerships(RECENT_STORAGE_KEY),
  );
  const [pinned, setPinned] = useState<StoredDealership[]>(() =>
    readStoredDealerships(PINNED_STORAGE_KEY),
  );

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    if (!activeDealer) return;
    setRecent((current) => {
      const next = putFirst(current, activeDealer, MAX_RECENT);
      writeStoredDealerships(RECENT_STORAGE_KEY, next);
      return next;
    });
  }, [activeDealer?.id, activeDealer?.name, activeDealer?.status]);

  const directoryQuery = useQuery({
    queryKey: ["dealership-switcher", debouncedSearch],
    queryFn: () =>
      adminApi.listDealershipDirectory({
        search: debouncedSearch || undefined,
        archived: "active",
        page: 1,
        pageSize: 20,
        sort: debouncedSearch ? "name" : "updatedAt",
        direction: debouncedSearch ? "asc" : "desc",
      }),
    enabled: open && Boolean(debouncedSearch),
  });

  const setSwitcherOpen = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  const selectDealership = (dealership: StoredDealership) => {
    const next = putFirst(recent, dealership, MAX_RECENT);
    setRecent(next);
    writeStoredDealerships(RECENT_STORAGE_KEY, next);
    setSwitcherOpen(false);
    navigate(`/dealerships/${dealership.id}/overview`);
  };

  const togglePinned = (dealership: StoredDealership) => {
    const isPinned = pinned.some((item) => item.id === dealership.id);
    const next = isPinned
      ? pinned.filter((item) => item.id !== dealership.id)
      : putFirst(pinned, dealership, MAX_PINNED);
    setPinned(next);
    writeStoredDealerships(PINNED_STORAGE_KEY, next);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between px-3"
        onClick={() => setSwitcherOpen(true)}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{activeDealer?.name ?? "Choose dealership"}</span>
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </Button>

      <Sheet open={open} onOpenChange={setSwitcherOpen}>
        <SheetContent side="left">
          <SheetHeader>
            <SheetTitle>Switch dealership</SheetTitle>
            <SheetDescription>
              Search any active dealership, or return to a recent or pinned workspace.
            </SheetDescription>
          </SheetHeader>
          <div className="px-5 pt-2">
            <SearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name or legal name"
              aria-label="Search dealership switcher"
              autoFocus
            />
          </div>
          <SheetBody className="px-3">
            {!debouncedSearch ? (
              <div className="space-y-5">
                <DealershipSection
                  title="Pinned"
                  empty="Pin the dealerships you return to most."
                  dealerships={pinned}
                  pinned={pinned}
                  onSelect={selectDealership}
                  onTogglePinned={togglePinned}
                />
                <DealershipSection
                  title="Recent"
                  empty="Dealerships you open will appear here."
                  dealerships={recent.filter(
                    (dealership) => !pinned.some((item) => item.id === dealership.id),
                  )}
                  pinned={pinned}
                  onSelect={selectDealership}
                  onTogglePinned={togglePinned}
                />
                <p className="px-3 text-xs leading-5 text-muted-foreground">
                  Search above to find any active dealership.
                </p>
              </div>
            ) : directoryQuery.isLoading ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">Loading dealerships…</p>
            ) : directoryQuery.isError ? (
              <div className="px-3 py-4 text-sm">
                <p className="text-destructive">Couldn't load dealerships.</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => directoryQuery.refetch()}>
                  Retry
                </Button>
              </div>
            ) : (directoryQuery.data?.items ?? []).length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {debouncedSearch ? `No dealerships match “${debouncedSearch}”.` : "No dealerships yet."}
              </p>
            ) : (
              <DealershipSection
                title="Search results"
                dealerships={directoryQuery.data?.items ?? []}
                pinned={pinned}
                onSelect={selectDealership}
                onTogglePinned={togglePinned}
              />
            )}
            {debouncedSearch && (directoryQuery.data?.total ?? 0) > 20 ? (
              <Button asChild variant="ghost" className="mt-3 w-full">
                <Link to={`/dealerships${debouncedSearch ? `?q=${encodeURIComponent(debouncedSearch)}` : ""}`} onClick={() => setSwitcherOpen(false)}>
                  View all {directoryQuery.data?.total.toLocaleString()} dealerships
                </Link>
              </Button>
            ) : null}
            <div className="mt-5 border-t px-2 pt-3">
              <Button asChild variant="ghost" className="w-full justify-start">
                <Link to="/dealerships" onClick={() => setSwitcherOpen(false)}>
                  <Building2 className="h-4 w-4" />
                  Open dealership directory
                </Link>
              </Button>
              <Button asChild variant="ghost" className="w-full justify-start">
                <Link to="/dealerships/new" onClick={() => setSwitcherOpen(false)}>
                  <Plus className="h-4 w-4" />
                  Create dealership
                </Link>
              </Button>
            </div>
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}

function DealershipSection({
  title,
  empty,
  dealerships,
  pinned,
  onSelect,
  onTogglePinned,
}: {
  title: string;
  empty?: string;
  dealerships: readonly StoredDealership[];
  pinned: readonly StoredDealership[];
  onSelect: (dealership: StoredDealership) => void;
  onTogglePinned: (dealership: StoredDealership) => void;
}) {
  return (
    <section aria-labelledby={`switcher-${title.toLowerCase().replaceAll(" ", "-")}`}>
      <h3
        id={`switcher-${title.toLowerCase().replaceAll(" ", "-")}`}
        className="mb-1 px-3 text-xs font-medium text-muted-foreground"
      >
        {title}
      </h3>
      {dealerships.length === 0 ? (
        empty ? <p className="px-3 py-2 text-sm text-muted-foreground">{empty}</p> : null
      ) : (
        <div className="flex flex-col gap-1">
          {dealerships.map((dealership) => {
            const isPinned = pinned.some((item) => item.id === dealership.id);
            return (
              <div
                key={dealership.id}
                className="flex min-h-touch items-center gap-1 rounded-md hover:bg-muted focus-within:bg-muted"
              >
                <button
                  type="button"
                  onClick={() => onSelect(dealership)}
                  className="min-w-0 flex-1 px-3 py-2 text-left text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <span className="block truncate font-medium">{dealership.name}</span>
                  <Badge
                    variant={operationalStatusVariant(dealership.status, "dealership")}
                    className="mt-1"
                  >
                    {dealership.status}
                  </Badge>
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mr-1 shrink-0"
                  aria-label={`${isPinned ? "Unpin" : "Pin"} ${dealership.name}`}
                  aria-pressed={isPinned}
                  onClick={() => onTogglePinned(dealership)}
                >
                  <Star className={isPinned ? "h-4 w-4 fill-current" : "h-4 w-4"} />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function putFirst(
  current: readonly StoredDealership[],
  dealership: StoredDealership,
  limit: number,
): StoredDealership[] {
  return [dealership, ...current.filter((item) => item.id !== dealership.id)].slice(0, limit);
}

function readStoredDealerships(key: string): StoredDealership[] {
  if (typeof window === "undefined") return [];
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(key) ?? "[]");
    if (!Array.isArray(value)) return [];
    return value.flatMap((item) => {
      if (typeof item !== "object" || item === null) return [];
      const candidate = item as Record<string, unknown>;
      const status = DealershipStatusSchema.safeParse(candidate.status);
      if (
        typeof candidate.id !== "string" ||
        typeof candidate.name !== "string" ||
        !status.success
      ) {
        return [];
      }
      return [{ id: candidate.id, name: candidate.name, status: status.data }];
    });
  } catch {
    return [];
  }
}

function writeStoredDealerships(key: string, dealerships: readonly StoredDealership[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(dealerships));
  } catch {
    // Storage can be unavailable in private or locked-down browser contexts.
  }
}
